import React, { useEffect, useRef } from 'react'
// @ts-expect-error - EffectContainer module doesn't exist yet, EffectProps type is unresolved
import { EffectProps } from './EffectContainer'

interface GridPoint {
    x: number
    y: number
    z: number
    wave: number
    intensity: number
    connected: number[]
}

interface WaveEntity {
    id: number
    originX: number
    originY: number
    radius: number
    maxRadius: number
    intensity: number
    color: number
    speed: number
    thickness: number
    birth: number
    directionX: number
    directionY: number
    spread: number
}

export function BubbleGridEffect({ clickPower, canvasRef, timeRef }: EffectProps) {
    const gridRef = useRef<GridPoint[][]>([])
    const wavesRef = useRef<WaveEntity[]>([])
    const waveIdRef = useRef(0)
    const lastClickPowerRef = useRef(0)

    useEffect(() => {
        if (!canvasRef.current) return
        if (clickPower === 0) {
            // Clear waves when power is 0
            wavesRef.current = []
            gridRef.current = []
            return
        }

        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        // Set canvas size much larger for diffuse effect
        const size = 400 + clickPower * 30
        canvas.width = size
        canvas.height = size

        // Initialize or update grid when canvas size changes
        const cols = 25
        const rows = 25

        if (
            gridRef.current.length !== cols ||
            (gridRef.current[0] && gridRef.current[0].length !== rows)
        ) {
            gridRef.current = []

            for (let i = 0; i < cols; i++) {
                gridRef.current[i] = []
                for (let j = 0; j < rows; j++) {
                    gridRef.current[i][j] = {
                        x: (canvas.width / cols) * i + canvas.width / cols / 2,
                        y: (canvas.height / rows) * j + canvas.height / rows / 2,
                        z: 0,
                        wave: 0,
                        intensity: 0,
                        connected: [],
                    }
                }
            }
        } else {
            // Update existing grid coordinates if canvas was resized
            for (let i = 0; i < cols; i++) {
                for (let j = 0; j < rows; j++) {
                    gridRef.current[i][j].x = (canvas.width / cols) * i + canvas.width / cols / 2
                    gridRef.current[i][j].y = (canvas.height / rows) * j + canvas.height / rows / 2
                }
            }
        }

        // Create new wave when click power increases
        if (clickPower > lastClickPowerRef.current) {
            const angle = Math.random() * Math.PI * 2
            const newWave: WaveEntity = {
                id: waveIdRef.current++,
                originX: canvas.width / 2,
                originY: canvas.height / 2,
                radius: 0,
                maxRadius: Math.max(canvas.width, canvas.height) * 1.5,
                intensity: 1,
                color: Math.random() * 360,
                speed: 1.2 + Math.random() * 0.6,
                thickness: 35 + lastClickPowerRef.current * 3, // Use previous clickPower like original
                birth: timeRef.current,
                directionX: Math.cos(angle),
                directionY: Math.sin(angle),
                spread: 0.3 + Math.random() * 0.4,
            }

            wavesRef.current.push(newWave)

            // Keep more waves active to maintain continuous effect
            if (wavesRef.current.length > 20) {
                // Increased from 10 to 20
                wavesRef.current.shift()
            }
        }

        lastClickPowerRef.current = clickPower

        const animate = () => {
            timeRef.current += 0.02

            // Clear canvas completely for pure transparency
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            const cols = gridRef.current.length
            const rows = gridRef.current[0]?.length || 0

            // Update wave entities
            wavesRef.current = wavesRef.current.filter(wave => {
                wave.radius += wave.speed * 0.4
                wave.intensity *= 0.995 // Slower decay (was 0.99)

                wave.originX += wave.directionX * wave.speed * 0.25
                wave.originY += wave.directionY * wave.speed * 0.25

                const offCanvas =
                    wave.originX < -200 ||
                    wave.originX > canvas.width + 200 ||
                    wave.originY < -200 ||
                    wave.originY > canvas.height + 200
                return !offCanvas && wave.radius < wave.maxRadius && wave.intensity > 0.005 // Lower threshold
            })

            // Reset grid points
            for (let i = 0; i < cols; i++) {
                for (let j = 0; j < rows; j++) {
                    const point = gridRef.current[i][j]
                    point.z = 0
                    point.wave = 0
                    point.intensity = 0
                }
            }

            // Apply each wave entity to the grid
            wavesRef.current.forEach(wave => {
                const age = timeRef.current - wave.birth

                for (let i = 0; i < cols; i++) {
                    for (let j = 0; j < rows; j++) {
                        const point = gridRef.current[i][j]
                        const dx = point.x - wave.originX
                        const dy = point.y - wave.originY

                        // Calculate angle relative to wave direction
                        const angleToPoint = Math.atan2(dy, dx)
                        const waveAngle = Math.atan2(wave.directionY, wave.directionX)
                        let angleDiff = angleToPoint - waveAngle

                        // Normalize angle difference to [-PI, PI]
                        while (angleDiff > Math.PI) angleDiff -= Math.PI * 2
                        while (angleDiff < -Math.PI) angleDiff += Math.PI * 2

                        // Directional intensity based on angle
                        const directionInfluence = (Math.cos(angleDiff * wave.spread) + 1) / 2

                        const distToHead = Math.sqrt(dx * dx + dy * dy)

                        // Bubble chain influence area
                        const tailLength = wave.radius * 2

                        // Check if point is within comet influence
                        const inFront = Math.cos(angleDiff) > -0.5
                        const distFromAxis = Math.abs(Math.sin(angleDiff) * distToHead)
                        const alongAxis = Math.cos(angleDiff) * distToHead

                        // Comet shape: wide at head, narrow at tail
                        const progress = Math.max(
                            0,
                            Math.min(1, (wave.radius - alongAxis) / tailLength),
                        )
                        const maxWidth = wave.thickness * 2 * (1 + progress * 2)

                        if (
                            inFront &&
                            alongAxis < wave.radius + wave.thickness &&
                            alongAxis > wave.radius - tailLength &&
                            distFromAxis < maxWidth
                        ) {
                            // Intensity based on position in comet
                            const headDist = Math.abs(alongAxis - wave.radius)
                            const intensity =
                                (1 - headDist / tailLength) *
                                (1 - distFromAxis / maxWidth) *
                                directionInfluence
                            const waveHeight = intensity * wave.intensity

                            // Add wave contribution
                            const oscillation = Math.sin(distToHead * 0.1 - age * 3)
                            point.z += waveHeight * 30 * (1 + oscillation * 0.2)
                            point.wave += waveHeight
                            point.intensity = Math.max(point.intensity, waveHeight * intensity)
                        }
                    }
                }
            })

            // Add subtle background animation only where there's activity
            if (clickPower > 0) {
                for (let i = 0; i < cols; i++) {
                    for (let j = 0; j < rows; j++) {
                        const point = gridRef.current[i][j]
                        // Only animate points that have some wave influence
                        if (point.intensity > 0.01) {
                            const flowX = Math.sin(i * 0.1 + timeRef.current * 0.5)
                            const flowY = Math.cos(j * 0.1 + timeRef.current * 0.4)
                            point.z += flowX * flowY * 1.5 * point.intensity
                        }
                    }
                }
            }

            // Draw bubble chain directional waves
            wavesRef.current.forEach(wave => {
                const age = timeRef.current - wave.birth

                ctx.save()

                const waveAngle = Math.atan2(wave.directionY, wave.directionX)

                const hue1 = wave.color + age * 10

                // Draw a chain of bubbles
                const bubbleCount = 10
                const maxBubbleSize = wave.thickness * 1.8 // Restored original size
                const chainLength = wave.radius * 2.5

                for (let i = 0; i < bubbleCount; i++) {
                    const progress = i / (bubbleCount - 1)
                    const distance = wave.radius - progress * chainLength

                    // Bubble position with gentle floating motion
                    const wobbleX = Math.sin(age * 1.5 + i * 0.7) * wave.thickness * 0.2
                    const wobbleY = Math.cos(age * 1.2 + i * 0.5) * wave.thickness * 0.2

                    const bubbleX = wave.originX + Math.cos(waveAngle) * distance + wobbleX
                    const bubbleY = wave.originY + Math.sin(waveAngle) * distance + wobbleY

                    // Bubble size varies - start smaller
                    const sizeFactor = 0.6 - progress * 0.3 // Reduced from 1 - progress * 0.5
                    const bubbleRadius =
                        maxBubbleSize * sizeFactor * (1 + Math.sin(age * 2 + i * 0.3) * 0.15)

                    if (bubbleRadius < 3) continue

                    // Outer soft glow
                    const glowGradient = ctx.createRadialGradient(
                        bubbleX,
                        bubbleY,
                        0,
                        bubbleX,
                        bubbleY,
                        bubbleRadius * 2.5,
                    )

                    const bubbleHue = hue1 + progress * 40
                    const bubbleIntensity = wave.intensity * (0.4 - progress * 0.1) // Lower initial opacity

                    glowGradient.addColorStop(
                        0,
                        `hsla(${bubbleHue}, 80%, 55%, ${bubbleIntensity * 0.3})`,
                    )
                    glowGradient.addColorStop(
                        0.4,
                        `hsla(${bubbleHue + 15}, 75%, 50%, ${bubbleIntensity * 0.2})`,
                    )
                    glowGradient.addColorStop(
                        0.8,
                        `hsla(${bubbleHue + 30}, 70%, 45%, ${bubbleIntensity * 0.1})`,
                    )
                    glowGradient.addColorStop(1, `hsla(${bubbleHue + 45}, 65%, 40%, 0)`)

                    ctx.fillStyle = glowGradient
                    ctx.globalAlpha = bubbleIntensity * 0.6
                    ctx.beginPath()
                    ctx.arc(bubbleX, bubbleY, bubbleRadius * 2.5, 0, Math.PI * 2)
                    ctx.fill()

                    // Main bubble body
                    const bubbleGradient = ctx.createRadialGradient(
                        bubbleX - bubbleRadius * 0.3,
                        bubbleY - bubbleRadius * 0.3,
                        0,
                        bubbleX,
                        bubbleY,
                        bubbleRadius * 1.2,
                    )

                    bubbleGradient.addColorStop(
                        0,
                        `hsla(${bubbleHue}, 85%, 65%, ${bubbleIntensity * 0.2})`,
                    )
                    bubbleGradient.addColorStop(
                        0.4,
                        `hsla(${bubbleHue + 15}, 80%, 55%, ${bubbleIntensity * 0.4})`,
                    )
                    bubbleGradient.addColorStop(
                        0.7,
                        `hsla(${bubbleHue + 30}, 75%, 45%, ${bubbleIntensity * 0.5})`,
                    )
                    bubbleGradient.addColorStop(
                        1,
                        `hsla(${bubbleHue + 45}, 70%, 35%, ${bubbleIntensity * 0.2})`,
                    )

                    ctx.fillStyle = bubbleGradient
                    ctx.globalAlpha = bubbleIntensity * 0.7
                    ctx.beginPath()
                    ctx.arc(bubbleX, bubbleY, bubbleRadius, 0, Math.PI * 2)
                    ctx.fill()

                    // Bubble highlight
                    const highlightGradient = ctx.createRadialGradient(
                        bubbleX - bubbleRadius * 0.4,
                        bubbleY - bubbleRadius * 0.4,
                        0,
                        bubbleX - bubbleRadius * 0.2,
                        bubbleY - bubbleRadius * 0.2,
                        bubbleRadius * 0.5,
                    )
                    highlightGradient.addColorStop(
                        0,
                        `hsla(${bubbleHue}, 100%, 80%, ${bubbleIntensity * 0.5})`,
                    )
                    highlightGradient.addColorStop(
                        0.5,
                        `hsla(${bubbleHue + 10}, 95%, 75%, ${bubbleIntensity * 0.3})`,
                    )
                    highlightGradient.addColorStop(1, `hsla(${bubbleHue + 20}, 90%, 70%, 0)`)

                    ctx.fillStyle = highlightGradient
                    ctx.globalAlpha = bubbleIntensity * 0.6
                    ctx.beginPath()
                    ctx.arc(
                        bubbleX - bubbleRadius * 0.3,
                        bubbleY - bubbleRadius * 0.3,
                        bubbleRadius * 0.4,
                        0,
                        Math.PI * 2,
                    )
                    ctx.fill()

                    // Subtle bubble edge
                    ctx.strokeStyle = `hsla(${bubbleHue + 20}, 65%, 45%, ${bubbleIntensity * 0.3})`
                    ctx.lineWidth = 1
                    ctx.globalAlpha = bubbleIntensity * 0.4
                    ctx.beginPath()
                    ctx.arc(bubbleX, bubbleY, bubbleRadius, 0, Math.PI * 2)
                    ctx.stroke()
                }

                ctx.restore()
            })

            // Draw grid mesh affected by waves
            for (let i = 0; i < cols; i++) {
                for (let j = 0; j < rows; j++) {
                    const point = gridRef.current[i][j]
                    if (!point || point.x === undefined || point.y === undefined) continue

                    const opacity = Math.min(1, point.intensity * 3 + 0.05) // Lower base opacity

                    if (opacity > 0.05 && point.intensity > 0) {
                        // Lower threshold for better visibility
                        // Draw connections with wave-based colors
                        if (i < cols - 1 && j < rows - 1) {
                            const nextPointX = gridRef.current[i + 1][j]
                            const nextPointY = gridRef.current[i][j + 1]

                            // Find dominant wave affecting this point
                            let dominantWave = null
                            let maxInfluence = 0
                            wavesRef.current.forEach(wave => {
                                const dx = point.x - wave.originX
                                const dy = point.y - wave.originY
                                const dist = Math.sqrt(dx * dx + dy * dy)
                                const influence =
                                    Math.max(0, 1 - Math.abs(dist - wave.radius) / wave.thickness) *
                                    wave.intensity
                                if (influence > maxInfluence) {
                                    maxInfluence = influence
                                    dominantWave = wave
                                }
                            })

                            if (dominantWave) {
                                const hue =
                                    dominantWave.color + (timeRef.current - dominantWave.birth) * 15

                                // Colorful horizontal line
                                const hGradient = ctx.createLinearGradient(
                                    point.x,
                                    point.y + point.z,
                                    nextPointX.x,
                                    nextPointX.y + nextPointX.z,
                                )
                                hGradient.addColorStop(
                                    0,
                                    `hsla(${hue}, 90%, 45%, ${opacity * 0.4})`,
                                ) // Reduced from 0.6
                                hGradient.addColorStop(
                                    0.5,
                                    `hsla(${hue + 20}, 95%, 50%, ${opacity * 0.5})`,
                                ) // Reduced from 0.7
                                hGradient.addColorStop(
                                    1,
                                    `hsla(${hue + 40}, 90%, 45%, ${opacity * 0.4})`,
                                )

                                ctx.strokeStyle = hGradient
                                ctx.lineWidth = 0.8 + point.intensity * 2.5 // Thicker lines for visibility
                                ctx.globalAlpha = Math.min(1, opacity * 0.7) // Reduced from 0.9
                                ctx.beginPath()
                                ctx.moveTo(point.x, point.y + point.z)
                                ctx.lineTo(nextPointX.x, nextPointX.y + nextPointX.z)
                                ctx.stroke()

                                // Colorful vertical line
                                const vGradient = ctx.createLinearGradient(
                                    point.x,
                                    point.y + point.z,
                                    nextPointY.x,
                                    nextPointY.y + nextPointY.z,
                                )
                                vGradient.addColorStop(
                                    0,
                                    `hsla(${hue + 30}, 90%, 45%, ${opacity * 0.4})`,
                                ) // Reduced from 0.6
                                vGradient.addColorStop(
                                    0.5,
                                    `hsla(${hue + 50}, 95%, 50%, ${opacity * 0.5})`,
                                ) // Reduced from 0.7
                                vGradient.addColorStop(
                                    1,
                                    `hsla(${hue + 70}, 90%, 45%, ${opacity * 0.4})`,
                                )

                                ctx.strokeStyle = vGradient
                                ctx.beginPath()
                                ctx.moveTo(point.x, point.y + point.z)
                                ctx.lineTo(nextPointY.x, nextPointY.y + nextPointY.z)
                                ctx.stroke()
                            }
                        }
                    }
                }
            }

            // Draw nodes at wave intersections
            for (let i = 0; i < cols; i += 2) {
                for (let j = 0; j < rows; j += 2) {
                    const point = gridRef.current[i][j]
                    if (!point || point.x === undefined || point.y === undefined) continue

                    if (point.intensity > 0.05 && point.x > 0 && point.y > 0) {
                        // Lower threshold for nodes
                        // Check how many waves are affecting this point
                        let waveCount = 0
                        let combinedHue = 0

                        wavesRef.current.forEach(wave => {
                            const dx = point.x - wave.originX
                            const dy = point.y - wave.originY
                            const dist = Math.sqrt(dx * dx + dy * dy)
                            const ringDist = Math.abs(dist - wave.radius)

                            if (ringDist < wave.thickness * 2) {
                                waveCount++
                                combinedHue += wave.color
                            }
                        })

                        if (waveCount > 0) {
                            combinedHue /= waveCount
                            const size = 1 + point.intensity * 3 + waveCount * 0.5

                            // Enhanced glow for wave intersections
                            const intersectionGlow = ctx.createRadialGradient(
                                point.x,
                                point.y + point.z,
                                0,
                                point.x,
                                point.y + point.z,
                                size * 3,
                            )

                            const brightness = 40 + waveCount * 8
                            const saturation = 85 + waveCount * 5
                            intersectionGlow.addColorStop(
                                0,
                                `hsla(${combinedHue}, ${saturation}%, ${brightness}%, ${point.intensity})`,
                            )
                            intersectionGlow.addColorStop(
                                0.4,
                                `hsla(${combinedHue + 30}, ${saturation}%, ${brightness + 5}%, ${
                                    point.intensity * 0.8
                                })`,
                            )
                            intersectionGlow.addColorStop(
                                1,
                                `hsla(${combinedHue + 60}, ${saturation - 5}%, ${
                                    brightness + 10
                                }%, 0)`,
                            )

                            ctx.fillStyle = intersectionGlow
                            ctx.globalAlpha = Math.min(1, point.intensity * 1.2) // Lower initial opacity for nodes
                            ctx.beginPath()
                            ctx.arc(point.x, point.y + point.z, size * 3, 0, Math.PI * 2)
                            ctx.fill()

                            // Strong core for multi-wave intersections
                            if (waveCount > 1) {
                                ctx.fillStyle = `hsla(${combinedHue}, 100%, 35%, ${
                                    (point.intensity * waveCount) / 2
                                })`
                                ctx.globalAlpha = Math.min(1, point.intensity * 1.5) // Lower initial opacity for cores
                                ctx.beginPath()
                                ctx.arc(point.x, point.y + point.z, size * 0.6, 0, Math.PI * 2)
                                ctx.fill()
                            }
                        }
                    }
                }
            }

            // Draw tiny floating sparkles around bubble chains
            wavesRef.current.forEach(wave => {
                const sparkleCount = 20
                const waveAngle = Math.atan2(wave.directionY, wave.directionX)
                const age = timeRef.current - wave.birth

                for (let i = 0; i < sparkleCount; i++) {
                    const progress = i / sparkleCount
                    const distance = wave.radius - progress * wave.radius * 2.5

                    // Very gentle floating motion
                    const floatRadius = wave.thickness * 0.8
                    const floatAngle = age * 0.8 + (i * Math.PI * 2) / 5
                    const floatX = Math.sin(floatAngle) * floatRadius
                    const floatY = Math.cos(floatAngle * 0.7) * floatRadius

                    const x = wave.originX + Math.cos(waveAngle) * distance + floatX
                    const y = wave.originY + Math.sin(waveAngle) * distance + floatY

                    const sparkleIntensity =
                        wave.intensity * (1 - progress * 0.3) * (0.3 + Math.sin(age * 2 + i) * 0.3)

                    // Tiny glowing sparkles
                    const sparkleGradient = ctx.createRadialGradient(x, y, 0, x, y, 2)
                    sparkleGradient.addColorStop(
                        0,
                        `hsla(${wave.color + i * 18}, 100%, 70%, ${sparkleIntensity})`,
                    )
                    sparkleGradient.addColorStop(1, `hsla(${wave.color + i * 18}, 100%, 60%, 0)`)

                    ctx.fillStyle = sparkleGradient
                    ctx.globalAlpha = sparkleIntensity
                    ctx.beginPath()
                    ctx.arc(x, y, 1 + Math.random() * 1.5, 0, Math.PI * 2)
                    ctx.fill()
                }
            })

            // Reset composite operation
            ctx.globalCompositeOperation = 'source-over'
            ctx.globalAlpha = 1

            animationFrameRef.current = requestAnimationFrame(animate)
        }

        const animationFrameRef = { current: 0 as number }
        animate()

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
            }
        }
    }, [clickPower, canvasRef, timeRef])

    return null
}
